// src/context/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from "react";
import API from "../api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if refresh token exists in cookies
  const hasRefreshToken = () => document.cookie.includes("refreshToken=");

  // Clear auth state safely
  const clearAuthState = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  // Refresh access token using httpOnly cookie
  const refreshAccessToken = async () => {
    try {
      if (!hasRefreshToken()) return false;
      await API.post("/auth/refresh-token", {}, { withCredentials: true });
      return true;
    } catch (err) {
      console.error("Refresh token failed:", err.response?.data || err.message);
      return false;
    }
  };

  // Fetch current user from server (/auth/me)
  const fetchUser = async () => {
    try {
      const res = await API.get("/auth/me", { withCredentials: true });
      if (res.data?.user) {
        setUser(res.data.user);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        return true;
      }
      clearAuthState(); // user deleted or invalid
      return false;
    } catch (err) {
      clearAuthState();
      return false;
    }
  };

  // Initialize auth on mount
  useEffect(() => {
    let refreshInterval;

    const initAuth = async () => {
      try {
        // 1️⃣ Refresh access token if refresh token exists
        const refreshed = await refreshAccessToken();

        // 2️⃣ Fetch user from server after refresh
        const fetched = await fetchUser();

        // If refresh or fetch fails, fallback to localStorage (optional)
        if (!refreshed && !fetched) {
          const storedUser = localStorage.getItem("user");
          if (storedUser) setUser(JSON.parse(storedUser));
        }
      } catch {
        clearAuthState();
      } finally {
        setLoading(false);
      }

      // 3️⃣ Optional: periodically refresh token
      if (hasRefreshToken()) {
        refreshInterval = setInterval(async () => {
          const ok = await refreshAccessToken();
          if (!ok) clearAuthState();
        }, 14 * 60 * 1000); // every 14 minutes
      }
    };

    initAuth();

    return () => refreshInterval && clearInterval(refreshInterval);
  }, []);

  // Login function (unchanged - works for both email and OAuth)
  const login = async (form) => {
    try {
      const res = await API.post("/auth/login", form, {
        withCredentials: true,
      });
      if (res.data?.user) {
        setUser(res.data.user);
        localStorage.setItem("user", JSON.stringify(res.data.user));

        // Ensure user exists on server
        await fetchUser();

        if (res.data.user.role === "admin") {
          window.location.href = "/admin/dashboard";
        } else {
          window.location.href = "/user/dashboard";
        }
      }
      return {
        success: true,
        message: res.data?.message || "Login successful",
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Login failed",
      };
    }
  };

  // NEW: Google OAuth login function (additive, doesn't change existing)
  const googleLogin = async (credential) => {
    try {
      const res = await API.post(
        "/auth/google",
        { credential },
        { withCredentials: true }
      );

      if (res.data?.success && res.data?.user) {
        setUser(res.data.user);
        localStorage.setItem("user", JSON.stringify(res.data.user));

        // Ensure user exists on server
        await fetchUser();

        // Same redirect logic as regular login
        if (res.data.user.role === "admin") {
          window.location.href = "/admin/dashboard";
        } else {
          window.location.href = "/user/dashboard";
        }

        return {
          success: true,
          user: res.data.user,
          message: res.data?.message || "Google login successful",
        };
      }

      return {
        success: false,
        message: res.data?.message || "Google login failed",
      };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || "Google login failed",
      };
    }
  };

  // Logout function (unchanged)
  const logout = async () => {
    try {
      await API.post("/auth/logout", {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout failed:", err.response?.data || err.message);
    } finally {
      clearAuthState();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        googleLogin, // NEW: Add Google login function
        logout,
        fetchUser,
        refreshAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook (unchanged)
export const useAuth = () => useContext(AuthContext);
